import { Search, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  SCOPE_LABELS,
  type EventFilterState,
  type FilterScope,
} from "@/lib/event-filter";

// 130 Spectator Filter -- the chip strip (My Kids / My Schools / My Favourites /
// All) + multi-select sport pills + free-text search. Controlled; the page owns
// state + URL sync. Personal chips are hidden when the user has no data for
// them (passed in `scopes`).

interface EventsFilterProps {
  value: EventFilterState;
  onChange: (next: EventFilterState) => void;
  /** Which scope chips to render (degrades for users without kids/roles). */
  scopes: FilterScope[];
  /** Sport names offered as multi-select pills. */
  availableSports: string[];
}

export function EventsFilter({ value, onChange, scopes, availableSports }: EventsFilterProps) {
  function setScope(scope: FilterScope) {
    onChange({ ...value, scope });
  }

  function toggleSport(sport: string) {
    const next = value.sports.includes(sport)
      ? value.sports.filter((s) => s !== sport)
      : [...value.sports, sport];
    onChange({ ...value, sports: next });
  }

  return (
    <div className="flex flex-col gap-3" data-component="events-filter">
      {/* Scope chips -- mutually exclusive (radio behaviour) */}
      <div className="flex flex-wrap items-center gap-1.5" role="radiogroup" aria-label="Event scope">
        {scopes.map((scope) => {
          const selected = value.scope === scope;
          return (
            <Button
              key={scope}
              type="button"
              size="sm"
              variant={selected ? "default" : "outline"}
              role="radio"
              aria-checked={selected}
              onClick={() => setScope(scope)}
              data-scope={scope}
              data-active={selected ? "true" : "false"}
            >
              {scope === "my_favourites" && (
                <Star className={cn("mr-1.5 h-3.5 w-3.5", selected && "fill-current")} />
              )}
              {SCOPE_LABELS[scope]}
            </Button>
          );
        })}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        {/* Free-text search */}
        <div className="relative sm:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={value.q}
            onChange={(e) => onChange({ ...value, q: e.target.value })}
            placeholder="Search events or schools"
            className="pl-8"
            aria-label="Search events"
            data-testid="events-filter-search"
          />
        </div>

        {/* Sport pills -- multi-select */}
        {availableSports.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5" data-component="sport-picker">
            {availableSports.map((sport) => {
              const active = value.sports.includes(sport);
              return (
                <button
                  key={sport}
                  type="button"
                  onClick={() => toggleSport(sport)}
                  aria-pressed={active}
                  data-sport={sport}
                  data-active={active ? "true" : "false"}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-input text-muted-foreground hover:bg-accent",
                  )}
                >
                  {sport}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
